import { ProgramChange } from '@/services/ops/investments/Program.service';
import { userService } from '@/services/User.service';
import formatters from '@/utils/Formatters';
import { OpenInNewOutlined } from '@mui/icons-material';
import { Button, Divider, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useToggle } from '@vestwell-frontend/hooks';

import { ICellRendererParams } from 'ag-grid-community';
import { FC, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { ReEnrollmentConfirmModal } from '../ProgramDetailsAddPlan';
import { ReEnrollmentType } from '../ProgramDetailsAddPlan/ReEnrollmentModal.component';

const StyledDetail = styled('div')(({ theme }) => ({
  alignItems: 'flex-start',
  backgroundColor: theme.palette.grey[50],
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  paddingLeft: theme.spacing(7)
}));

type ProgramChangesDetailCellRendererProps = {
  cellParams: ICellRendererParams<ProgramChange>;
};

const DETAILS_INFO = [
  { field: 'sponsorPlanName', label: 'Plan Name' },
  { field: 'sponsorPlanId', label: 'Plan ID' },
  { field: 'reEnrollmentType', label: 'Re-enrollment Method' },
  { field: 'jiraTicket', label: 'Note' }
];

const getReenrollmentLabel = (reEnrollmentType: ReEnrollmentType) =>
  ({
    full: 'Full Re-enrollment',
    none: 'No Re-enrollment',
    partial: 'Partial Re-enrollment'
  })[reEnrollmentType];

export const ProgramChangesDetailCellRenderer: FC<
  ProgramChangesDetailCellRendererProps
> = props => {
  const navigate = useNavigate();
  const [isConfirmModalOpened, toggleConfirmModal] = useToggle(false);
  const isPending = props.cellParams.data.status === 'pending';
  const isAnotherApprover =
    userService.getUser()?.nickname !==
    props.cellParams.data.programChangeCreatedBy.id;

  const onReviewClick = useCallback(() => {
    if (props.cellParams.data.reEnrollmentType === 'partial') {
      navigate(
        `/ops/investments/re-enrollment-review/plan/${props.cellParams.data.sponsorPlanId}/program-change/${props.cellParams.data.programChangeId}`
      );
    } else {
      toggleConfirmModal();
    }
  }, [props.cellParams.data]);

  const submitApproveHistory = useMemo(() => {
    return [
      ...(props.cellParams.data.canceledBy
        ? [
            {
              actionLabel: 'Canceled',
              submittedAt: props.cellParams.data.canceledAt,
              submitter: props.cellParams.data.canceledBy.id
            }
          ]
        : []),
      ...(props.cellParams.data.approvedBy
        ? [
            {
              actionLabel:
                props.cellParams.data.status === 'approved' ||
                props.cellParams.data.canceledBy
                  ? 'Approved'
                  : 'Rejected',
              submittedAt: props.cellParams.data.approvedAt,
              submitter: props.cellParams.data.approvedBy.id
            }
          ]
        : []),
      {
        actionLabel: 'Submitted',
        submittedAt: props.cellParams.data.programChangeCreatedAt,
        submitter: props.cellParams.data.programChangeCreatedBy.id
      }
    ];
  }, []);

  return (
    <>
      <StyledDetail>
        <Stack spacing={2}>
          {DETAILS_INFO.map(e => (
            <Stack direction='row' key={e.field}>
              <Typography color='grey' minWidth={200} variant='body2'>
                {e.label}
              </Typography>
              <Typography color='black' variant='body2' width='75%'>
                {(e.field === 'reEnrollmentType'
                  ? getReenrollmentLabel(props.cellParams.data[e.field])
                  : props.cellParams.data[e.field]) || '-'}
              </Typography>
            </Stack>
          ))}
        </Stack>
        <Button
          endIcon={isPending && <OpenInNewOutlined />}
          onClick={onReviewClick}
          variant={isPending ? 'contained' : 'text'}>
          {isPending ? 'Review' : 'View change'}
        </Button>
        <Divider flexItem />
        <Stack spacing={1}>
          {submitApproveHistory.map(
            ({ actionLabel, submittedAt, submitter }) => (
              <Stack
                direction='row'
                divider={<Divider flexItem orientation='vertical' />}
                key={actionLabel}
                spacing={1}>
                <Typography variant='caption'>
                  {formatters.formatFromIsoDateCustom(
                    submittedAt,
                    'MM/DD/YYYY hh:mma'
                  )}
                </Typography>
                <Typography variant='caption'>
                  {actionLabel} by {submitter}
                </Typography>
              </Stack>
            )
          )}
        </Stack>
      </StyledDetail>
      <ReEnrollmentConfirmModal
        currentProgramId={props.cellParams.data.oldProgramId}
        currentProgramName={props.cellParams.data.oldProgramName}
        effectiveDate={props.cellParams.data.effectiveDate}
        effectiveDateNote={props.cellParams.data.jiraTicket}
        isAnotherApprover={isAnotherApprover}
        isForApprove
        isModalOpen={isConfirmModalOpened}
        newProgramId={props.cellParams.data.newProgramId}
        newProgramName={props.cellParams.data.newProgramName}
        onClose={toggleConfirmModal}
        onSuccess={toggleConfirmModal}
        planId={props.cellParams.data.sponsorPlanId}
        planName={props.cellParams.data.sponsorPlanName}
        programChangeId={props.cellParams.data.programChangeId}
        reEnrollmentType={props.cellParams.data.reEnrollmentType}
        status={props.cellParams.data.status}
      />
    </>
  );
};

ProgramChangesDetailCellRenderer.displayName =
  'ProgramChangesDetailCellRenderer';
